import { Navbar } from "../Navigation/Navbar";
import { HelpButton } from "../Common/HelpButton";
import { Button, Dropdown, Typography, Card, Modal, message, Tooltip } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import "../../Styles/styles-Reports.css";
import { useSelector } from "react-redux";
import { MultiMetricPicker } from "../Common/MultiMetricPicker.tsx";
import { IGeneralReportsConfig, IPlayerReportDataStreams, PLAYER_REPORT_DATA_STREAMS, REPORT_TYPES } from "./constants";
import GeneralReportConfig from "./GeneralReportConfig";
import { PresetType } from "../PresetSelection.tsx";
import { getTeamPresets, setTeamPresets } from "../../store/actions/team.actions.js";
import { useDispatch } from "react-redux";
import { SelectWithAdd } from "../Common/SelectWithAdd.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { trainingAPI } from "../../api/TrainingAPI.js";
import moment from "moment";

const { Text } = Typography;

export const GeneralReports = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const {
        info: {
            display_name: team_name,
        },
        presets
    } = useSelector(({ team }) => team);
    const { user } = useAuth0();

    const [selectedReportTypeKey, setSelectedReportTypeKey] = useState<string | undefined>(undefined);
    const [metricSelectorModalIsOpen, setMetricSelectorModalIsOpen] = useState<boolean>(false);
    const [useAggs, setUseAggs] = useState<boolean>(false);
    const [metricSelectorPrefix, setMetricSelectorPrefix] = useState<string | undefined>(undefined);

    const getFilteredReportList = () => {
        return REPORT_TYPES.filter((item) => {
            if (item.teams && item.teams.length > 0) {
                return item.teams.includes(team_name);
            }
            return true;
        })
    }

    const presetItemsOfInterest = presets?.filter((e: any) => e.type === PresetType.ReportSettings)
    const reportPresetItems = presetItemsOfInterest?.map((preset: any) => {
        return {
            label: preset.name,
            value: preset.name,
            disabled: preset.creator_id__email !== user?.email,
        }
    })
    const getPresetSelected = () => presetItemsOfInterest?.find((preset: any) => preset.name === currentSelectedTilePreset)

    const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState<string | undefined>(undefined)
    const [config, setConfig] = useState<IGeneralReportsConfig>(getPresetSelected()?.config || {});

    const addPreset = async (item: string) => {
        const newPreset = await trainingAPI.createPreset({ name: item, type: PresetType.ReportSettings, config });
        dispatch(setTeamPresets(newPreset))

        setCurrentSelectedTilePreset(item)
    }

    const deletePreset = async (item: string) => {
        const presetOfInterest = presetItemsOfInterest?.find((preset: any) => preset.name === item)
        await trainingAPI.deletePreset(presetOfInterest?.preset_id)

        const updatedPreset = presets.filter((preset: any) => preset.preset_id !== presetOfInterest?.preset_id)
        dispatch(setTeamPresets(updatedPreset))

        setCurrentSelectedTilePreset((updatedPreset && updatedPreset?.length > 0) ? updatedPreset[0].value : undefined)
    }

    const updatedPreset = async () => {
        const presetModified = presetItemsOfInterest?.find((preset: any) => preset.name === currentSelectedTilePreset)
        await trainingAPI.updatePreset({
            name: currentSelectedTilePreset, type: PresetType.ReportSettings, config, preset_id: presetModified?.preset_id
        });
        presetModified.config = config
        dispatch(setTeamPresets(presets))
        message.success("Saved!");
    }


    useEffect(() => {
        dispatch(getTeamPresets({
            type: [
                PresetType.CustomBreakdownReportMetrics,
                PresetType.GpsReportMetrics,
                PresetType.TeamReportMetrics,
                PresetType.PlayerReportMetrics,
                PresetType.TestingReportMetrics,
                PresetType.ForcePlateReportMetrics,
                PresetType.ReportSettings
            ]
        }) as any)
    }, [])

    useEffect(() => {
        if (currentSelectedTilePreset === undefined) {
            setConfig({})
            setSelectedReportTypeKey(undefined)
        } else {
            setConfig(getPresetSelected()?.config || { ...config })
            // change report type on preset change
            setSelectedReportTypeKey((getPresetSelected()?.config || { ...config })?.report_type)
        }
    }, [currentSelectedTilePreset])

    const menu = {
        items: getFilteredReportList(),
        onClick: (value: any) => {
            setConfig({ report_type: value.key });
            setSelectedReportTypeKey(value.key);
        },
    };

    const getSelectReportTypeObj = (key?: string) => {
        return REPORT_TYPES.find(x => x.key === key) || undefined;
    }

    const setConfigMetrics = (metric_list: any) => {
        setConfig({
            ...config,
            [`${metricSelectorPrefix}-metrics`]: metric_list
        });
    }

    // Unified report generation method
    const handleUnifiedReportSubmit = async () => {
        if (!selectedReportTypeKey) {
            message.error("Please select a report type");
            return;
        }

        if (!config.emails || config.emails.length === 0) {
            message.error("Please select at least one email recipient");
            return;
        }

        const reportObj = getSelectReportTypeObj(selectedReportTypeKey);

        if (!reportObj) {
            message.error("Invalid report type selected");
            return;
        }

        // Validate required parameters based on report type
        if (reportObj.requirements?.includes('date') && !config.date) {
            message.error("Please select a date for the report");
            return;
        }

        if (reportObj.requirements?.includes('date_range') && (!config.date_range || config.date_range.length !== 2)) {
            message.error("Please select a date range for the report");
            return;
        }

        if (reportObj.requirements?.includes('single_player_filter') && !config.player_filter) {
            message.error("Please select a player for the report");
            return;
        }

        if (reportObj.requirements?.includes('player_report_type_selector') && (!config.player_report_data_streams || config.player_report_data_streams.length === 0)) {
            message.error("Please select at least one data stream for the player report");
            return;
        }

        // Prepare the payload for the unified API
        const payload: any = {
            ...config,
            report_type: selectedReportTypeKey,
            emails: config.emails,
        };
        const selectedMetrics = (config as any)[`${selectedReportTypeKey}-metrics`]

        if (reportObj.key === 'player_report') {
            let configCorrect = true;

            if (config.player_report_data_streams) {
                config.player_report_data_streams.forEach((dataStream) => {
                    const needToBeCount = PLAYER_REPORT_DATA_STREAMS.find((e) => e.key === dataStream)?.count
                    if ((config as any)[`${dataStream}-metrics`] === undefined || (config as any)[`${dataStream}-metrics`].length !== needToBeCount) {
                        configCorrect = false
                    }
                })
            } else {
                configCorrect = false
            }

            if (configCorrect === false) {
                message.error(`Please select metrics`);
                return;
            }
        } else if (reportObj?.metric_selector_info?.count !== undefined && reportObj?.metric_selector_info?.count !== 0 && (selectedMetrics || []).length !== reportObj?.metric_selector_info?.count) {
            message.error(`Please select metrics`);
            return;
        }

        // Handle special case for weekly body weight report
        if (selectedReportTypeKey === 'weekly_body_weight_report') {
            payload.report_type = 'body_weight_report';  // Use same backend handler
            payload.weekly = true;  // Enable weekly mode
        }

        // Transform metrics data for the API
        if (selectedMetrics) {
            payload.metrics = selectedMetrics;
        }

        // Handle player report data streams metrics
        if (config.player_report_data_streams) {
            const dataStreamMetrics: any = {};
            config.player_report_data_streams.forEach((stream) => {
                if ((config as any)[`${stream}-metrics`]) {
                    dataStreamMetrics[stream] = (config as any)[`${stream}-metrics`];
                }
            });
            payload.player_report_metrics = dataStreamMetrics;
        }

        // Handle sessions parameter (needed for GPS reports)
        if ((config as any).sessions) {
            payload.sessions = (config as any).sessions;
        }

        setIsLoading(true);
        try {
            const response = await trainingAPI.generateGeneralReport(payload);
            message.success("The report has been sent to your email");
        } catch (error) {
            console.error("Report generation error:", error);
            message.error(typeof error === 'string' ? error : "Failed to generate report. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = () => {
        if (selectedReportTypeKey === 'single_week_distance_breakdown' && moment(config.date_range[1]).diff(moment(config.date_range[0]), 'days') > 7) {
            message.error(`Date range cannot exceed 7 days. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        } else if (selectedReportTypeKey === 'multi_week_distance_breakdown' && moment(config.date_range[1]).diff(moment(config.date_range[0]), 'days') > 49) {
            message.error(`Date range cannot exceed 7 weeks. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        } else if (selectedReportTypeKey === 'status_report') {
            const daysDiff = moment(config.date_range[1]).diff(moment(config.date_range[0]), 'days') + 1; // +1 to include both start and end dates
            if (daysDiff < 1 || daysDiff > 84) {
                message.error(`Status report date range must be between 1 and 84 days. Provided range is ${daysDiff} days: ${config.date_range[0]} to ${config.date_range[1]}`)
                return;
            }
        } else if (selectedReportTypeKey === 'weekly_body_weight_report' && moment(config.date_range[1]).diff(moment(config.date_range[0]), 'days') > 84) {
            message.error(`Date range cannot exceed 12 weeks. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        }

        // Use the unified report submit by default
        // You can switch to handleLegacyReportSubmit() for testing legacy functionality
        handleUnifiedReportSubmit();
    };

    return (
        <>
            <Navbar />
            <HelpButton page="Reports" />

            <Card
                title={<Text style={{ fontSize: 22 }}>Generate a Report</Text>}
                className="general-report-options-container"
            >
                <>
                    <div style={{ width: '700px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <div style={{ flexGrow: 1 }} className="preset-row">
                            <SelectWithAdd
                                items={reportPresetItems || []}
                                addItem={addPreset}
                                deleteItem={deletePreset}
                                placeholder="Select Preset"
                                setValue={setCurrentSelectedTilePreset}
                                currentSelectedTilePreset={currentSelectedTilePreset || undefined}
                                addItemLabel={"Add"}
                                addItemTextboxLabel={"Enter Preset Name"}
                                label={"Would you like to select a pre-configured report?"}
                                labelPaddingRight={10}
                                allowClear={true}
                            />
                        </div>

                        <div>
                            <Button type="primary" size="small"
                                onClick={updatedPreset}
                                disabled={false}
                                style={{ width: "100px" }}
                                className={''}
                            >
                                Save Preset
                            </Button>
                        </div>
                    </div>
                </>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>Please select the type of report and date you would like to generate: </span>
                    <div style={{ marginLeft: '10px' }}>
                        <Dropdown menu={menu} className="general-report-type-dropdown">
                            <Button
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {getSelectReportTypeObj(selectedReportTypeKey)?.label || "Select Report Type"} <CaretDownOutlined />
                            </Button>
                        </Dropdown>
                    </div>
                </div>

                <GeneralReportConfig
                    selectedReportTypeKey={selectedReportTypeKey}
                    getSelectReportTypeObj={getSelectReportTypeObj}
                    config={config}
                    setConfig={setConfig}
                    setMetricSelectorModalIsOpen={setMetricSelectorModalIsOpen}
                    setMetricSelectorPrefix={setMetricSelectorPrefix}
                    setUseAggs={setUseAggs}
                />

                <Button
                    style={{ float: 'right', marginTop: '10px' }}
                    type="primary"
                    onClick={handleSubmit}
                    loading={isLoading}
                    disabled={isLoading}
                >
                    Generate Report
                </Button>
            </Card>

            {metricSelectorModalIsOpen && (
                <Modal
                    title={`Configure Metrics: ${getSelectReportTypeObj(selectedReportTypeKey)?.label}`}
                    width={"60%"}
                    open={metricSelectorModalIsOpen}
                    onCancel={() => setMetricSelectorModalIsOpen(false)}
                    footer={null}
                >
                    <MultiMetricPicker
                        requirements={getSelectReportTypeObj(selectedReportTypeKey)}
                        setResult={setConfigMetrics}
                        noAsym={true}
                        setOpen={setMetricSelectorModalIsOpen}
                        showAggs={useAggs}
                        presetInfo={{ items: presets }}
                        config={config}
                        metricSelectorPrefix={metricSelectorPrefix}
                    />
                </Modal>

            )
            }
        </>
    )
}

export default GeneralReports